import { ArrowDown, ArrowUp, LayoutGrid, Plus, Trash2 } from 'lucide-react';
import type { RowNode } from '@news-cms/shared';
import { useEditor } from './editor-context';
import { ColumnNodeEditor } from './column-node-editor';
import { NodeToolbar } from './node-toolbar';

const GAP_OPTIONS = [0, 1, 2, 3, 4, 5];

export function RowNodeEditor({ node }: { node: RowNode }) {
  const { selectedId, select, addColumnToRow, updateRow, move, remove } = useEditor();
  const isSelected = selectedId === node.id;
  const totalSpan = node.children.reduce((s, c) => s + c.span, 0);
  const remainingSpan = 12 - totalSpan;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        select(node.id);
      }}
      className={`relative rounded-md border px-px pb-0.5 pt-3 transition ${
        isSelected
          ? 'border-emerald-400 bg-emerald-50/40 dark:border-emerald-500 dark:bg-emerald-950/20'
          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
      }`}
    >
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-wide text-slate-400">
        <div className="flex items-center gap-1.5">
          <LayoutGrid className="h-3 w-3" />
          Row · gap {node.gap} · span {totalSpan}/12
        </div>
        {remainingSpan > 0 && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            เหลือ {remainingSpan}/12
          </span>
        )}
      </div>

      <NodeToolbar
        visible={isSelected}
        extra={
          <div className="mr-1 flex items-center gap-1 border-r border-slate-200 pr-2 text-[10px] text-slate-500 dark:border-slate-700">
            gap
            <select
              value={node.gap}
              onChange={(e) => updateRow(node.id, { gap: Number(e.target.value) })}
              onClick={(e) => e.stopPropagation()}
              className="h-6 rounded border border-slate-200 bg-white px-1 text-xs dark:border-slate-700 dark:bg-slate-900"
            >
              {GAP_OPTIONS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>
        }
        buttons={[
          {
            key: 'up',
            label: 'เลื่อนขึ้น',
            icon: <ArrowUp className="h-3.5 w-3.5" />,
            onClick: () => move(node.id, 'up'),
          },
          {
            key: 'down',
            label: 'เลื่อนลง',
            icon: <ArrowDown className="h-3.5 w-3.5" />,
            onClick: () => move(node.id, 'down'),
          },
          {
            key: 'column',
            label: 'Column',
            icon: <Plus className="h-3.5 w-3.5" />,
            onClick: () => addColumnToRow(node.id, Math.max(1, Math.min(12, remainingSpan || 6))),
            disabled: node.children.length >= 12,
          },
          {
            key: 'delete',
            label: 'ลบ',
            icon: <Trash2 className="h-3.5 w-3.5" />,
            onClick: () => remove(node.id),
            variant: 'destructive',
          },
        ]}
      />

      {node.children.length === 0 ? (
        <div className="rounded border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-400 dark:border-slate-700">
          ว่าง — กด "Column" บน toolbar เพื่อเริ่ม
        </div>
      ) : (
        <div
          className="grid"
          style={{
            gridTemplateColumns: node.children.map((c) => `${c.span}fr`).join(' '),
            gap: `${node.gap * 4}px`,
          }}
        >
          {node.children.map((c) => (
            <ColumnNodeEditor key={c.id} node={c} />
          ))}
        </div>
      )}
    </div>
  );
}
